import { useCallback } from 'react'
import { useAnalyticsTrack } from '@/features/amplitude'
import {
  COMPLIANCE_EVENTS,
  VALUE_SAFE_QUESTION_KEYS,
  type AnalyticsSurface,
} from './compliance-analytics.constants'
import type {
  AnsweredIntakeQuestionProps,
  BlockedOnIncompleteSectionProps,
  ClickedReportCtaProps,
  CompletedIntakeSectionProps,
  ExpandedReportObligationProps,
  FailedIntakeSubmitProps,
  JumpedToReportStatusProps,
  RenderedReportProps,
  SelectedPlanProps,
  StartedIntakeProps,
  ViewedIntakePageProps,
  ViewedPlansPageProps,
  ViewedReportPageProps,
} from './compliance-analytics.types'

const isScalarValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean'

export interface ComplianceAnalytics {
  trackViewedIntakePage: (props: ViewedIntakePageProps) => void
  trackStartedIntake: (props: StartedIntakeProps) => void
  trackAnsweredQuestion: (props: AnsweredIntakeQuestionProps) => void
  trackCompletedIntakeSection: (props: CompletedIntakeSectionProps) => void
  trackBlockedOnIncompleteSection: (
    props: BlockedOnIncompleteSectionProps
  ) => void
  trackFailedIntakeSubmit: (props: FailedIntakeSubmitProps) => void
  trackViewedReportPage: (props: ViewedReportPageProps) => void
  trackRenderedReport: (props: RenderedReportProps) => void
  trackExpandedReportObligation: (props: ExpandedReportObligationProps) => void
  trackJumpedToReportStatus: (props: JumpedToReportStatusProps) => void
  trackClickedReportCta: (props: ClickedReportCtaProps) => void
  trackOpenedReportPrintDialog: () => void
  trackViewedPlansPage: (props: ViewedPlansPageProps) => void
  trackSelectedPlan: (props: SelectedPlanProps) => void
}

export const useComplianceAnalytics = (
  surface: AnalyticsSurface
): ComplianceAnalytics => {
  const track = useAnalyticsTrack()

  const trackViewedIntakePage = useCallback(
    (props: ViewedIntakePageProps): void => {
      track(COMPLIANCE_EVENTS.ViewedIntakePage, { ...props, surface })
    },
    [track, surface]
  )

  const trackStartedIntake = useCallback(
    (props: StartedIntakeProps): void => {
      track(COMPLIANCE_EVENTS.StartedIntake, { ...props, surface })
    },
    [track, surface]
  )

  const trackAnsweredQuestion = useCallback(
    (args: AnsweredIntakeQuestionProps): void => {
      const { value, ...rest } = args
      track(COMPLIANCE_EVENTS.AnsweredIntakeQuestion, {
        ...rest,
        surface,
        // Only the six keys that already flow to HubSpot carry their answer.
        ...(VALUE_SAFE_QUESTION_KEYS.has(args.question_key) &&
        isScalarValue(value)
          ? { value }
          : {}),
      })
    },
    [track, surface]
  )

  const trackCompletedIntakeSection = useCallback(
    (props: CompletedIntakeSectionProps): void => {
      track(COMPLIANCE_EVENTS.CompletedIntakeSection, { ...props, surface })
    },
    [track, surface]
  )

  const trackBlockedOnIncompleteSection = useCallback(
    (props: BlockedOnIncompleteSectionProps): void => {
      track(COMPLIANCE_EVENTS.BlockedOnIncompleteSection, { ...props, surface })
    },
    [track, surface]
  )

  const trackFailedIntakeSubmit = useCallback(
    (props: FailedIntakeSubmitProps): void => {
      track(COMPLIANCE_EVENTS.FailedIntakeSubmit, { ...props, surface })
    },
    [track, surface]
  )

  const trackViewedReportPage = useCallback(
    (props: ViewedReportPageProps): void => {
      track(COMPLIANCE_EVENTS.ViewedReportPage, { ...props, surface })
    },
    [track, surface]
  )

  const trackRenderedReport = useCallback(
    (props: RenderedReportProps): void => {
      track(COMPLIANCE_EVENTS.RenderedReport, { ...props, surface })
    },
    [track, surface]
  )

  const trackExpandedReportObligation = useCallback(
    (props: ExpandedReportObligationProps): void => {
      track(COMPLIANCE_EVENTS.ExpandedReportObligation, { ...props, surface })
    },
    [track, surface]
  )

  const trackJumpedToReportStatus = useCallback(
    (props: JumpedToReportStatusProps): void => {
      track(COMPLIANCE_EVENTS.JumpedToReportStatus, { ...props, surface })
    },
    [track, surface]
  )

  const trackClickedReportCta = useCallback(
    (props: ClickedReportCtaProps): void => {
      track(COMPLIANCE_EVENTS.ClickedReportCta, { ...props, surface })
    },
    [track, surface]
  )

  const trackOpenedReportPrintDialog = useCallback((): void => {
    track(COMPLIANCE_EVENTS.OpenedReportPrintDialog, { surface })
  }, [track, surface])

  const trackViewedPlansPage = useCallback(
    (props: ViewedPlansPageProps): void => {
      track(COMPLIANCE_EVENTS.ViewedPlansPage, { ...props, surface })
    },
    [track, surface]
  )

  const trackSelectedPlan = useCallback(
    (props: SelectedPlanProps): void => {
      track(COMPLIANCE_EVENTS.SelectedPlan, { ...props, surface })
    },
    [track, surface]
  )

  // Not memoized: every caller destructures the methods, which are already
  // stable, so the object identity is never observed.
  return {
    trackViewedIntakePage,
    trackStartedIntake,
    trackAnsweredQuestion,
    trackCompletedIntakeSection,
    trackBlockedOnIncompleteSection,
    trackFailedIntakeSubmit,
    trackViewedReportPage,
    trackRenderedReport,
    trackExpandedReportObligation,
    trackJumpedToReportStatus,
    trackClickedReportCta,
    trackOpenedReportPrintDialog,
    trackViewedPlansPage,
    trackSelectedPlan,
  }
}
